import { lastValueFrom } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';

import { PORTAL_API_BASE_URL } from '@echospaces-web-monorepo/es-portal-shell';

export type PlaceholderUsedAs =
  | 'image' | 'slideshow' | 'video' | 'sound'
  | 'radio' | 'object' | 'text' | 'info';

export interface Link {
  id: string;
  placeholderId: string;
  contentId: string;
  usedAs: PlaceholderUsedAs;
  versionId: string | null;
  options?: unknown;
  createdDate: string;
  updatedDate: string;
}

export interface CreateLinkPayload {
  placeholderUsedAs: PlaceholderUsedAs;
  options?: unknown;
}

/**
 * Per-island state for placeholder ↔ content links. The list comes from
 * `GET /api/ecms/links/:islandSpaceId` (one fetch covers the whole island
 * subtree); writes go through `PUT /api/ecms/links/.../placeholders/...`
 * and `DELETE` on the same path. The placeholders tab uses
 * `byPlaceholderId` to mark each placeholder as linked or empty.
 */
@Injectable({ providedIn: 'root' })
export class LinkService {
  private readonly http = inject(HttpClient);
  private readonly api = inject(PORTAL_API_BASE_URL);

  private readonly _links = signal<Link[]>([]);
  private readonly _loading = signal(false);
  private currentIslandId: string | null = null;

  readonly links = this._links.asReadonly();
  readonly loading = this._loading.asReadonly();

  /**
   * Map `placeholderId → Link` for the currently loaded island. One link
   * per placeholder (per version) — server enforces the uniqueness.
   */
  readonly byPlaceholderId = computed<Map<string, Link>>(() => {
    const map = new Map<string, Link>();
    for (const link of this._links()) map.set(link.placeholderId, link);
    return map;
  });

  async loadForIsland(islandSpaceId: string): Promise<void> {
    this._loading.set(true);
    this.currentIslandId = islandSpaceId;
    try {
      const data = await lastValueFrom(
        this.http.get<Link[]>(`${this.api}/ecms/links/${islandSpaceId}`),
      );
      this._links.set(data);
    } finally {
      this._loading.set(false);
    }
  }

  async createLink(
    islandSpaceId: string,
    placeholderId: string,
    contentId: string,
    payload: CreateLinkPayload,
  ): Promise<void> {
    await lastValueFrom(
      this.http.put(
        `${this.api}/ecms/links/${islandSpaceId}/placeholders/${placeholderId}/content-objects/${contentId}`,
        payload,
      ),
    );
    if (this.currentIslandId === islandSpaceId) {
      await this.loadForIsland(islandSpaceId);
    }
  }

  async removeLink(islandSpaceId: string, placeholderId: string): Promise<void> {
    await lastValueFrom(
      this.http.delete(`${this.api}/ecms/links/${islandSpaceId}/placeholders/${placeholderId}`),
    );
    if (this.currentIslandId === islandSpaceId) {
      await this.loadForIsland(islandSpaceId);
    }
  }

  clear(): void {
    this._links.set([]);
    this.currentIslandId = null;
  }
}
